import React from "react";
import { Link } from "react-router-dom";

/* Shared page building blocks for Theme C (Nova). */

export function Eyebrow({ children, className = "" }) {
  return (
    <div className={"mono text-[11px] uppercase tracking-[0.25em] grad-text font-semibold " + className}>
      {children}
    </div>
  );
}

export function PageHero({ eyebrow, heading, highlight, intro, index = "01" }) {
  // `highlight` is an optional substring in `heading` that receives the gradient.
  let renderedHeading = heading;
  if (highlight && heading.includes(highlight)) {
    const parts = heading.split(highlight);
    renderedHeading = (
      <>
        {parts[0]}
        <span className="grad-text">{highlight}</span>
        {parts[1]}
      </>
    );
  }
  return (
    <section className="relative overflow-hidden" data-testid="page-hero">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl blob"
           style={{ background: "radial-gradient(circle at 30% 40%, #4F46E5 0%, #06B6D4 40%, transparent 70%)" }} />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-16 md:pb-20 relative">
        <div className="up inline-flex items-center gap-2 panel-2 rounded-full px-4 py-2 mb-8 border hairline">
          <span className="w-2 h-2 rounded-full grad-bg" />
          <span className="mono text-[11px] uppercase tracking-[0.18em] text-[#0A0E1A]/80">{index} · {eyebrow}</span>
        </div>
        <h1 className="display up text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight max-w-5xl mb-8" data-testid="page-hero-heading">
          {renderedHeading}
        </h1>
        {intro && (
          <p className="text-base md:text-xl leading-[1.5] text-[#0A0E1A]/75 max-w-3xl">{intro}</p>
        )}
      </div>
    </section>
  );
}

export function Section({ children, eyebrow, heading, highlight, body, className = "", bg = "bg-white", indexLabel }) {
  let renderedHeading = heading;
  if (highlight && heading?.includes?.(highlight)) {
    const parts = heading.split(highlight);
    renderedHeading = (
      <>
        {parts[0]}
        <span className="grad-text">{highlight}</span>
        {parts[1]}
      </>
    );
  }
  return (
    <section className={"border-t hairline " + bg + " " + className}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
        {(eyebrow || heading || body) && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-7">
              {eyebrow && <Eyebrow className="mb-4">{indexLabel ? `${indexLabel} · ` : ""}{eyebrow}</Eyebrow>}
              {heading && (
                <h2 className="display text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.95]">
                  {renderedHeading}
                </h2>
              )}
            </div>
            {body && (
              <p className="lg:col-span-5 text-base md:text-lg text-[#0A0E1A]/70 leading-relaxed">{body}</p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export function CardGrid({ children, cols = 3 }) {
  const cls = cols === 4 ? "md:grid-cols-2 lg:grid-cols-4" : cols === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3";
  return <div className={"grid grid-cols-1 " + cls + " gap-5"}>{children}</div>;
}

export function FeatureCard({ index, title, body, footer, featured = false, to, pill }) {
  const content = (
    <div
      className={"card-hover rounded-[20px] p-8 md:p-10 relative overflow-hidden h-full " + (featured ? "grad-bg text-white border hairline" : "grad-border grad-border-on bg-white")}
      data-testid={to ? `card-${to.replace(/[^a-z0-9]/gi, "-")}` : undefined}
    >
      <div className="flex items-baseline justify-between mb-6">
        <span className={"mono text-[11px] font-bold tracking-[0.22em] " + (featured ? "text-white/90" : "grad-text")}>
          {index || ""}
        </span>
        {pill && (
          <span className={"mono text-[10px] tracking-[0.18em] " + (featured ? "text-white/70" : "text-[#0A0E1A]/50")}>
            {pill}
          </span>
        )}
      </div>
      <h3 className={"display text-2xl md:text-3xl font-bold mb-4 leading-tight " + (featured ? "text-white" : "")}>
        {title}
      </h3>
      <p className={"text-sm md:text-base leading-relaxed " + (featured ? "text-white/85" : "text-[#0A0E1A]/70")}>
        {body}
      </p>
      {footer && <div className="mt-5">{footer}</div>}
      {to && (
        <div className={"mt-6 mono text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 " + (featured ? "text-white" : "text-[#4F46E5]")}>
          Learn more <span>→</span>
        </div>
      )}
    </div>
  );
  if (to) return <Link to={to} className="block h-full">{content}</Link>;
  return content;
}

export function ButtonPrimary({ to, children, onClick, type, ...rest }) {
  const cls = "grad-bg text-white rounded-full px-6 py-3.5 text-sm font-semibold shadow-[0_14px_40px_-12px_rgba(79,70,229,0.4)] hover:shadow-[0_20px_56px_-12px_rgba(79,70,229,0.7)] transition-shadow inline-flex items-center gap-2";
  if (to) return <Link to={to} className={cls} {...rest}>{children} <span>→</span></Link>;
  return <button type={type || "button"} onClick={onClick} className={cls} {...rest}>{children} <span>→</span></button>;
}

export function ButtonSecondary({ to, children, onClick, type, ...rest }) {
  const cls = "bg-white rounded-full px-6 py-3.5 text-sm font-semibold border hairline hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors inline-flex items-center gap-2";
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  return <button type={type || "button"} onClick={onClick} className={cls} {...rest}>{children}</button>;
}

export function CtaBand({ heading, body, ctas = [] }) {
  return (
    <section className="border-t hairline" data-testid="cta-band">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
        <div className="grad-bg rounded-[28px] p-10 md:p-16 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl"/>
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              {heading && (
                <h2 className="display text-3xl md:text-5xl lg:text-6xl font-bold leading-[0.95] text-white">
                  {heading}
                </h2>
              )}
            </div>
            <div className="lg:col-span-4 flex flex-col gap-5">
              {body && <p className="text-base md:text-lg text-white/85 leading-relaxed">{body}</p>}
              <div className="flex flex-col gap-3">
                {ctas.map((c, i) => {
                  const primary = c.variant === "primary" || i === 0;
                  const cls = primary
                    ? "bg-white text-[#0A0E1A] rounded-full px-6 py-4 text-sm font-semibold hover:bg-[#0A0E1A] hover:text-white transition-colors flex items-center justify-between"
                    : "border border-white/50 text-white rounded-full px-6 py-4 text-sm font-semibold hover:bg-white hover:text-[#0A0E1A] transition-colors flex items-center justify-between";
                  return <Link key={i} to={c.to} className={cls}>{c.label} <span>→</span></Link>;
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Crumbs({ trail }) {
  return (
    <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#0A0E1A]/60">
      {trail.map((t, i) => (
        <React.Fragment key={i}>
          {t.to ? <Link to={t.to} className="hover:text-[#4F46E5]">{t.label}</Link> : <span>{t.label}</span>}
          {i < trail.length - 1 && <span className="mx-2 text-[#4F46E5]">/</span>}
        </React.Fragment>
      ))}
    </div>
  );
}
